/**
 * LayerInfoModal + LayerInfoButton — explains what each map layer does
 * and where the data comes from. Triggered by a small ⓘ icon next to
 * each toggle row in the Layers menu (2D and 3D).
 *
 * Centralised content lives in components/layerInfo.ts so we don't
 * scatter copy throughout the layer toggle JSX.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LAYER_INFO, type LayerInfo } from './layerInfo';

interface LayerInfoButtonProps {
  /** Key into LAYER_INFO. If unknown, the button is rendered but a
   *  fallback "no info available" message shows on click. */
  layerKey: string;
}

/** Small inline ⓘ button that opens the LayerInfoModal for a given layer. */
export function LayerInfoButton({ layerKey }: LayerInfoButtonProps) {
  const [open, setOpen] = useState(false);
  const info = LAYER_INFO[layerKey];

  // Rendered as a span (not a <button>) so it can safely nest inside the
  // existing layer-toggle <button> in App.tsx / MapSettings3D without
  // producing invalid HTML. Keyboard support is provided manually via
  // role/tabIndex/onKeyDown.
  const handleOpen = (e: { stopPropagation: () => void; preventDefault?: () => void }) => {
    e.stopPropagation();
    e.preventDefault?.();
    setOpen(true);
  };

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        className="layer-info-btn"
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleOpen(e);
        }}
        aria-label={`About ${info?.title || 'this layer'}`}
        title={`About ${info?.title || 'this layer'}`}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v2h-2zm0 4h2v6h-2z" />
        </svg>
      </span>
      {open && (
        <LayerInfoModal info={info} layerKey={layerKey} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

interface LayerInfoModalProps {
  info: LayerInfo | undefined;
  layerKey: string;
  onClose: () => void;
}

function LayerInfoModal({ info, layerKey, onClose }: LayerInfoModalProps) {
  // Esc to close — standard modal behaviour.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Portal to document.body so the modal escapes any ancestor with
  // transform/mask-image/filter (e.g. the dropdown menu it was launched
  // from), which would otherwise constrain its position: fixed overlay.
  return createPortal(
    <div
      className="layer-info-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="layer-info-title"
    >
      <div
        className="layer-info-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="layer-info-modal-header">
          <h3 id="layer-info-title" className="layer-info-modal-title">
            {info?.title || layerKey}
          </h3>
          <button
            type="button"
            className="layer-info-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="layer-info-modal-body">
          {info ? (
            <>
              <p className="layer-info-text">{info.description}</p>
              {info.why && (
                <div className="layer-info-section">
                  <div className="layer-info-section-label">Why it matters</div>
                  <p className="layer-info-text">{info.why}</p>
                </div>
              )}
              {info.caveat && (
                <div className="layer-info-section layer-info-caveat">
                  <div className="layer-info-section-label">⚠️ Limitations</div>
                  <p className="layer-info-text">{info.caveat}</p>
                </div>
              )}
              <div className="layer-info-meta">
                {info.source && (
                  <div>
                    <span className="layer-info-meta-label">Source:</span>{' '}
                    <span>{info.source}</span>
                  </div>
                )}
                {info.updateFrequency && (
                  <div>
                    <span className="layer-info-meta-label">Updates:</span>{' '}
                    <span>{info.updateFrequency}</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="layer-info-text">No information available for this layer.</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default LayerInfoButton;
